import { UsageMode } from '@shared/core/users'

/**
 * Stable, position-independent identifiers for the settings tabs.
 * The URL carries `?tab=<name>`; positions are legacy only.
 */
export type SettingsTabName =
  | 'default-template'
  | 'templates'
  | 'plan'
  | 'billing'
  | 'calendars'
  | 'other-settings'
  | 'booking-page'

const SETTINGS_TAB_NAMES: SettingsTabName[] = [
  'default-template',
  'templates',
  'plan',
  'billing',
  'calendars',
  'other-settings',
  'booking-page',
]

/**
 * The 1-based `?tab=N` layouts as they resolve today, keyed by `usage_mode`.
 *
 * These are frozen historical maps, not a description of the current tab order.
 * They exist so that links minted before names existed keep landing exactly
 * where they land today. In particular, `?tab=2` means Plan only in `customer`
 * mode; in `personal` mode it means Templates. That mismatch is a pre-existing
 * bug and it is preserved here on purpose.
 *
 * Always resolve a number against the FULL layout below, never against the
 * tabs currently visible to the user, or hiding a tab would silently shift
 * every legacy link after it.
 */
const LEGACY_TAB_ORDER_BY_USAGE_MODE: Record<UsageMode, SettingsTabName[]> = {
  customer: ['templates', 'plan', 'billing', 'calendars', 'other-settings', 'booking-page'],
  personal: ['default-template', 'templates', 'plan', 'billing', 'calendars', 'booking-page'],
}

export type ResolvedSettingsTab = {
  /** The tab to select. Always one of `visibleTabNames`. */
  selected: SettingsTabName
  /** True when the URL should be rewritten to `?tab=<selected>` with `replace: true`. */
  shouldReplaceUrl: boolean
}

function isSettingsTabName(value: string): value is SettingsTabName {
  return (SETTINGS_TAB_NAMES as string[]).includes(value)
}

function resolveLegacyTabName(rawTab: string, usageMode: UsageMode | undefined): SettingsTabName | null {
  if (!/^\d+$/.test(rawTab)) {
    return null
  }

  // Mirrors the tab builder, which treats every non-`customer` mode as the
  // unshift layout. Keep the two in step or a number can resolve to a tab the
  // user does not have.
  const legacyOrder =
    usageMode === 'customer' ? LEGACY_TAB_ORDER_BY_USAGE_MODE.customer : LEGACY_TAB_ORDER_BY_USAGE_MODE.personal

  return legacyOrder[Number(rawTab) - 1] ?? null
}

/**
 * Resolve the `?tab=` search param to a visible tab.
 *
 * Callers MUST wait for the profile to load before calling this: `usage_mode`
 * decides how a legacy number resolves, and guessing it misroutes the
 * `personal` cohort.
 */
export function resolveSettingsTab(
  rawTab: string | null,
  usageMode: UsageMode | undefined,
  visibleTabNames: SettingsTabName[],
): ResolvedSettingsTab {
  const firstVisible = visibleTabNames[0]

  if (!rawTab) {
    return { selected: firstVisible, shouldReplaceUrl: false }
  }

  if (isSettingsTabName(rawTab)) {
    return visibleTabNames.includes(rawTab)
      ? { selected: rawTab, shouldReplaceUrl: false }
      : { selected: firstVisible, shouldReplaceUrl: true }
  }

  const legacyName = resolveLegacyTabName(rawTab, usageMode)

  if (legacyName && visibleTabNames.includes(legacyName)) {
    return { selected: legacyName, shouldReplaceUrl: true }
  }

  return { selected: firstVisible, shouldReplaceUrl: true }
}
